import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Globe, MapPin, Edit2, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import api from '../../../api';

export default function PartnerDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [partner, setPartner] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        try {
            setLoading(true);
            const partnersData = await api.getPartners({ limit: 1000 });
            const foundPartner = partnersData.find(p => p.id === id);
            setPartner(foundPartner);

            if (foundPartner) {
                const eventsData = await api.getEvents();
                // Filter events where organizer matches partner name
                const associated = eventsData.filter(e => 
                    e.organizer && e.organizer.toLowerCase() === foundPartner.name.toLowerCase()
                );
                setEvents(associated);
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this partner?')) return;

        try {
            await api.deletePartner(id);
            navigate('/admin', { state: { activeTab: 'partners' } });
        } catch (error) {
            alert('Error deleting partner: ' + error.message);
        }
    };

    if (loading) {
        return (
            <main className="main">
                <div className="skeleton" style={{ height: '100vh' }} />
            </main>
        );
    }

    if (!partner) {
        return (
            <main className="main">
                <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
                    <h1>Partner not found</h1>
                    <button onClick={() => navigate('/admin', { state: { activeTab: 'partners' } })} className="btn btn-primary" style={{ marginTop: '20px' }}>
                        Back to Partners
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main className="main" style={{ background: 'var(--bg-admin)', minHeight: '100vh' }}>
            <div className="container" style={{ maxWidth: '900px', padding: '40px 24px' }}>
                {/* Back Button */}
                <button
                    onClick={() => navigate('/admin', { state: { activeTab: 'partners' } })}
                    className="btn btn-ghost"
                    style={{ marginBottom: '24px', padding: '8px 16px' }}
                >
                    <ArrowLeft size={18} /> Back to Partners
                </button>

                {/* Partner Detail Card */}
                <div className="card" style={{ padding: '40px', marginBottom: '24px' }}>
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        marginBottom: '32px',
                        paddingBottom: '24px',
                        borderBottom: '1px solid var(--border)'
                    }}>
                        <div style={{ flex: 1 }}>
                            <div style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '50%',
                                background: 'var(--primary-gradient)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#000',
                                fontSize: '40px',
                                fontWeight: '700',
                                marginBottom: '20px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}>
                                {partner.name.charAt(0)}
                            </div>
                            <h1 style={{
                                fontSize: '32px',
                                fontWeight: '700',
                                color: 'var(--text-primary)',
                                marginBottom: '12px'
                            }}>
                                {partner.name}
                            </h1>
                            <span style={{
                                display: 'inline-block',
                                background: 'var(--bg-secondary)',
                                padding: '8px 20px',
                                borderRadius: '20px',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: 'var(--text-secondary)'
                            }}>
                                {partner.category}
                            </span>
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <Link
                                to={`/admin/partners/edit/${partner.id}`}
                                className="btn btn-secondary"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                <Edit2 size={18} />
                                Edit
                            </Link>
                            <button
                                onClick={handleDelete}
                                className="btn btn-secondary"
                                style={{
                                    color: 'var(--error)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <Trash2 size={18} />
                                Delete
                            </button>
                        </div>
                    </div>

                    {/* Description */}
                    {partner.description && (
                        <div style={{ marginBottom: '32px' }}>
                            <h3 style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                marginBottom: '12px',
                                color: 'var(--text-primary)'
                            }}>
                                About
                            </h3>
                            <p style={{
                                fontSize: '15px',
                                lineHeight: '1.7',
                                color: 'var(--text-secondary)'
                            }}>
                                {partner.description}
                            </p>
                        </div>
                    )}

                    {/* Art Forms */}
                    {partner.artforms && partner.artforms.length > 0 && (
                        <div style={{ marginBottom: '32px' }}>
                            <h3 style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                marginBottom: '12px',
                                color: 'var(--text-primary)'
                            }}>
                                Art Forms
                            </h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                {partner.artforms.map((art, idx) => (
                                    <span key={idx} style={{
                                        display: 'inline-block',
                                        background: 'var(--bg-secondary)',
                                        padding: '10px 18px',
                                        borderRadius: '12px',
                                        fontSize: '14px',
                                        color: 'var(--text-primary)',
                                        fontWeight: '500'
                                    }}>
                                        {art}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Services */}
                    {partner.services && partner.services.length > 0 && (
                        <div style={{ marginBottom: '32px' }}>
                            <h3 style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                marginBottom: '12px',
                                color: 'var(--text-primary)'
                            }}>
                                Services
                            </h3>
                            <ul style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: 0
                            }}>
                                {partner.services.map((service, idx) => (
                                    <li key={idx} style={{
                                        fontSize: '15px',
                                        color: 'var(--text-secondary)',
                                        marginBottom: '10px',
                                        paddingLeft: '24px',
                                        position: 'relative'
                                    }}>
                                        <span style={{
                                            position: 'absolute',
                                            left: 0,
                                            color: 'var(--primary-admin)',
                                            fontWeight: '700'
                                        }}>•</span>
                                        {service}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Contact Information */}
                    <div style={{
                        background: 'var(--bg-secondary)',
                        padding: '28px',
                        borderRadius: '16px'
                    }}>
                        <h3 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            marginBottom: '20px',
                            color: 'var(--text-primary)'
                        }}>
                            Contact Information
                        </h3>

                        {partner.contact_person && (
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '15px' }}>
                                    {partner.contact_person}
                                </div>
                                {partner.role && (
                                    <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                                        {partner.role}
                                    </div>
                                )}
                            </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {partner.email && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Mail size={20} color="var(--primary-admin)" />
                                    <a href={`mailto:${partner.email}`} style={{
                                        color: 'var(--primary-admin)',
                                        textDecoration: 'none',
                                        fontSize: '15px',
                                        fontWeight: '500'
                                    }}>
                                        {partner.email}
                                    </a>
                                </div>
                            )}

                            {partner.phone && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Phone size={20} color="var(--primary-admin)" />
                                    <a href={`tel:${partner.phone}`} style={{
                                        color: 'var(--text-primary)',
                                        textDecoration: 'none',
                                        fontSize: '15px'
                                    }}>
                                        {partner.phone}
                                    </a>
                                </div>
                            )}

                            {partner.website && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Globe size={20} color="var(--primary-admin)" />
                                    <a
                                        href={`https://${partner.website}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            color: 'var(--primary-admin)',
                                            textDecoration: 'none',
                                            fontSize: '15px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        {partner.website}
                                    </a>
                                </div>
                            )}

                            {partner.location && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <MapPin size={20} color="var(--primary-admin)" />
                                    <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                                        {partner.location}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Associated Events Section */}
                <div className="card" style={{ padding: '40px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '20px', fontWeight: '700', margin: 0, color: 'var(--text-primary)' }}>
                            Associated Events
                        </h3>
                        <span style={{ 
                            background: 'var(--bg-secondary)', 
                            padding: '4px 12px', 
                            borderRadius: '20px', 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: 'var(--text-secondary)'
                        }}>
                            {events.length} Event{events.length !== 1 ? 's' : ''}
                        </span>
                    </div>

                    {events.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', background: 'var(--bg-secondary)', borderRadius: '16px' }}>
                            <p style={{ color: 'var(--text-muted)' }}>No events listed under this partner yet.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
                            {events.map((event) => (
                                <Link key={event.id} to={`/events/${event.id}`} style={{ textDecoration: 'none' }}>
                                    <div className="admin-card-premium" style={{ padding: '0', overflow: 'hidden', height: '100%', transition: 'all 0.3s ease' }}>
                                        <div style={{ height: '140px', overflow: 'hidden', position: 'relative' }}>
                                            <img 
                                                src={event.image_url || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400'} 
                                                alt={event.title}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                            <div style={{ position: 'absolute', top: 10, right: 10 }}>
                                                <span style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', color: '#fff', fontSize: '10px', padding: '2px 8px', borderRadius: '10px' }}>
                                                    {event.category}
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{ padding: '16px' }}>
                                            <h4 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)', lineClamp: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                {event.title}
                                            </h4>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                                                <Edit2 size={12} />
                                                <span>{format(new Date(event.date), 'MMM d, yyyy')}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}

